/* eslint-disable react/require-default-props */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { Input as InputPrimitive } from '@base-ui-components/react/input';
import { cva } from 'class-variance-authority';

import { Field, FieldDescription } from 'shared/components/ihcl/v2/field';
import { Label } from 'shared/components/ihcl/v2/label';
import { cn } from 'shared/lib/utils';

const inputSizeVariants = cva(
  'w-full min-w-0 bg-transparent outline-none text-black font-medium placeholder:text-gray-placeholder disabled:cursor-not-allowed',
  {
    variants: {
      size: {
        default: 'text-base',
        compact: 'text-sm',
        tiny: 'text-sm',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const fieldBoxVariants = cva(
  'flex items-center w-full border border-solid bg-white transition-colors font-medium cursor-text',
  {
    variants: {
      size: {
        default: 'h-14 px-4 gap-3 rounded-lg',
        compact: 'h-12 px-4 gap-3 rounded-lg',
        tiny: 'h-10 px-3 gap-2.5 rounded-md',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

type VisualState = 'default' | 'focus' | 'success' | 'error' | 'disabled';

function getVisualState(
  disabled: boolean,
  status: 'default' | 'error' | 'success',
  focused: boolean
): VisualState {
  if (disabled) return 'disabled';
  if (status !== 'default') return status;
  if (focused) return 'focus';
  return 'default';
}

function getStateClasses(state: VisualState) {
  switch (state) {
    case 'focus':
      return {
        fieldBox: 'bg-gray-background border-primary/60',
        label: 'text-primary60',
        caption: 'text-primary60',
      };
    case 'success':
      return {
        fieldBox: 'bg-positive00 border-positive/60',
        label: 'text-positive70',
        caption: 'text-positive70',
      };
    case 'error':
      return {
        fieldBox: 'bg-negative00 border-negative/60',
        label: 'text-negative70',
        caption: 'text-negative70',
      };
    case 'disabled':
      return {
        fieldBox: 'bg-gray-input-background border-transparent',
        label: 'text-gray-placeholder',
        caption: 'text-gray-placeholder',
      };
    default:
      return {
        fieldBox: 'bg-white border-body-copy-gray/24',
        label: 'text-gray-label',
        caption: 'text-gray-label',
      };
  }
}

type InputProps = {
  label?: string;
  caption?: string;
  status?: 'default' | 'error' | 'success';
  disabled?: boolean;
  size?: 'default' | 'compact' | 'tiny';
  startAddon?: React.ReactNode;
  endAddon?: React.ReactNode;
  className?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>;

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      caption,
      status = 'default',
      disabled = false,
      size = 'default',
      startAddon,
      endAddon,
      className,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      id: idProp,
      ...inputProps
    },
    ref
  ) => {
    const generatedId = useId();
    const id = idProp || generatedId;
    const [focused, setFocused] = useState(false);
    const [internalValue, setInternalValue] = useState(defaultValue ?? '');

    const localRef = useRef<HTMLInputElement | null>(null);
    const setRef = useCallback(
      (node: HTMLInputElement | null) => {
        localRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          // eslint-disable-next-line no-param-reassign
          ref.current = node;
        }
      },
      [ref]
    );

    // Sync with the DOM value on mount so uncontrolled consumers that set the
    // initial value imperatively via the ref (e.g. react-hook-form's register)
    // float the label instead of showing the placeholder overlay.
    useEffect(() => {
      if (value !== undefined) return;
      const domValue = localRef.current?.value;
      if (domValue && domValue !== internalValue) {
        setInternalValue(domValue);
      }
      // Intentionally mount-only: syncs the imperatively-set initial DOM value.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currentValue = value !== undefined ? value : internalValue;
    const hasValue = currentValue !== '' && currentValue !== undefined;

    const visualState = getVisualState(disabled, status, focused);
    const stateClasses = getStateClasses(visualState);

    const showFloatingLabel =
      size === 'default' && label && (focused || hasValue);
    const showPlaceholderLabel =
      size === 'default' && label && !focused && !hasValue;
    const showStaticLabel =
      (size === 'compact' || size === 'tiny') && label && !focused && !hasValue;

    return (
      <Field
        orientation="vertical"
        className={cn('gap-2', 'w-full', className)}
        data-disabled={disabled || undefined}
        data-invalid={status === 'error' || undefined}
      >
        <div
          data-slot="input-field-box"
          data-state={visualState}
          className={cn(
            fieldBoxVariants({ size }),
            stateClasses.fieldBox,
            disabled && 'cursor-not-allowed'
          )}
          onClick={() => {
            document.getElementById(id)?.focus();
          }}
        >
          {startAddon && <div className="shrink-0">{startAddon}</div>}
          <div className="relative flex min-w-0 flex-1 flex-col justify-center">
            {showFloatingLabel && (
              <Label
                htmlFor={id}
                className={cn(
                  'pointer-events-none text-sm leading-[1.2] font-medium tracking-wide',
                  stateClasses.label
                )}
              >
                {label}
              </Label>
            )}
            {showPlaceholderLabel && (
              <Label
                htmlFor={id}
                className={cn(
                  'pointer-events-none text-base leading-[1.2] font-medium tracking-wide',
                  stateClasses.label
                )}
              >
                {label}
              </Label>
            )}
            {showStaticLabel && (
              <Label
                htmlFor={id}
                className={cn(
                  'pointer-events-none text-sm leading-[1.2] font-medium tracking-wide',
                  stateClasses.label
                )}
              >
                {label}
              </Label>
            )}
            <InputPrimitive
              ref={setRef}
              id={id}
              data-slot="input"
              disabled={disabled}
              value={value}
              defaultValue={value !== undefined ? undefined : defaultValue}
              className={cn(
                inputSizeVariants({ size }),
                (showPlaceholderLabel || showStaticLabel) && 'sr-only'
              )}
              onChange={(e) => {
                if (value === undefined) {
                  setInternalValue(e.target.value);
                }
                onChange?.(e);
              }}
              onFocus={(e) => {
                setFocused(true);
                onFocus?.(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                onBlur?.(e);
              }}
              {...inputProps}
            />
          </div>
          {endAddon && <div className="shrink-0">{endAddon}</div>}
        </div>
        {caption && (
          <FieldDescription
            className={cn(
              'text-sm leading-[1.2] font-medium tracking-wide',
              size === 'tiny' ? 'px-3' : 'px-4',
              stateClasses.caption
            )}
          >
            {caption}
          </FieldDescription>
        )}
      </Field>
    );
  }
);

Input.displayName = 'Input';

export {
  Input,
  inputSizeVariants,
  fieldBoxVariants,
  getVisualState,
  getStateClasses,
};
export type { VisualState };
